import { Change } from './layerTypes'

/**
 * Says in words what the comparison found: `position.x` went from 120 to 132
 * becomes "Moved 12px right". Here rather than in the renderer because it is
 * product copy, and a renderer release should not stand between someone
 * deciding the list reads badly and the page.
 *
 * Related properties are gathered back together, having been reported apart so
 * that a caller wanting only the horizontal movement need not parse a sentence.
 */

const round = (value: number) => Math.round(value * 10) / 10

const quote = (value: string) => `“${value}”`

const asNumber = (value?: Change['before']) =>
  typeof value === 'number' ? value : 0

const asText = (value?: Change['before']) =>
  typeof value === 'string' ? value : ''

/** The change for one property path, or undefined when it did not change. */
const find = (changes: readonly Change[], property: string) =>
  changes.find(change => change.property === property)

/** Whether anything under a path changed - `style.fills` covers `.0.color`. */
const touched = (changes: readonly Change[], prefix: string) =>
  changes.some(
    change =>
      change.property === prefix || change.property.startsWith(`${prefix}.`)
  )

function describeMove(changes: readonly Change[]) {
  const x = find(changes, 'position.x')
  const y = find(changes, 'position.y')

  if (!x && !y) return null

  const dx = x ? round(asNumber(x.after) - asNumber(x.before)) : 0
  const dy = y ? round(asNumber(y.after) - asNumber(y.before)) : 0

  // Which way, rather than a signed number: "-200x" reads as two hundred times
  const horizontal =
    dx === 0 ? '' : `${Math.abs(dx)}px ${dx < 0 ? 'left' : 'right'}`
  const vertical = dy === 0 ? '' : `${Math.abs(dy)}px ${dy < 0 ? 'up' : 'down'}`

  // Both rounded away, which leaves a bare "Moved" saying less than nothing
  if (!horizontal && !vertical) return null

  const gap = horizontal && vertical ? ' and ' : ''

  return `Moved ${horizontal}${gap}${vertical}`
}

function describeResize(changes: readonly Change[]) {
  const width = find(changes, 'size.width')
  const height = find(changes, 'size.height')

  // Where it started as well: "Resized to 500 wide" does not say bigger
  if (!width) {
    if (!height) return null

    return `Resized from ${round(asNumber(height.before))}px to ${round(
      asNumber(height.after)
    )}px tall`
  }

  if (!height) {
    return `Resized from ${round(asNumber(width.before))}px to ${round(
      asNumber(width.after)
    )}px wide`
  }

  // The side that did not change is named too, or it reads as half a size
  const from = `${round(asNumber(width.before))}px × ${round(
    asNumber(height.before)
  )}px`
  const to = `${round(asNumber(width.after))}px × ${round(
    asNumber(height.after)
  )}px`

  return `Resized from ${from} to ${to}`
}

function describeShape(change: Change) {
  if (!asText(change.before)) return 'Shape added'
  if (!asText(change.after)) return 'Shape removed'
  return 'Shape changed'
}

function describeImages(change: Change) {
  const before = asText(change.before).split(', ').filter(Boolean)
  const after = asText(change.after).split(', ').filter(Boolean)

  if (before.length === 0) {
    return after.length === 1 ? 'Image added' : 'Images added'
  }

  if (after.length === 0) {
    return before.length === 1 ? 'Image removed' : 'Images removed'
  }

  return before.length === 1 && after.length === 1
    ? 'Image replaced'
    : 'Images changed'
}

/**
 * Everything one changed layer or entity has to say, a phrase at a time, in
 * the order a reader looks for it: what it is and whether it is still there,
 * then where and how big, then how it is painted.
 */
export function listChanges(changes: readonly Change[]): string[] {
  const said: string[] = []

  // From, not to: the row is already titled with the name it has now, so the
  // one worth saying is the one the reader cannot see
  const name = find(changes, 'name')
  if (name) {
    const was = asText(name.before)
    said.push(was ? `Renamed from ${quote(was)}` : 'Named')
  }

  const hidden = find(changes, 'hidden')
  if (hidden) said.push(hidden.after === true ? 'Hidden' : 'Shown')

  const locked = find(changes, 'locked')
  if (locked) said.push(locked.after === true ? 'Locked' : 'Unlocked')

  const text = find(changes, 'text')
  if (text) {
    said.push(
      `Text changed from ${quote(asText(text.before))} to ${quote(
        asText(text.after)
      )}`
    )
  }

  const moved = describeMove(changes)
  if (moved) said.push(moved)

  const resized = describeResize(changes)
  if (resized) said.push(resized)

  const rotation = find(changes, 'rotation')
  if (rotation) {
    said.push(
      `Rotated from ${round(asNumber(rotation.before))}° to ${round(
        asNumber(rotation.after)
      )}°`
    )
  }

  const shape = find(changes, 'shape')
  if (shape) said.push(describeShape(shape))

  const images = find(changes, 'images')
  if (images) said.push(describeImages(images))

  if (touched(changes, 'corners')) said.push('Corners changed')

  // One phrase each however many of their own properties differ: the canvas
  // beside the list is what shows a reader told "fill changed" what it became
  if (touched(changes, 'style.fills')) said.push('Fill changed')
  if (touched(changes, 'style.borders')) said.push('Border changed')
  if (touched(changes, 'style.shadows')) said.push('Shadow changed')
  if (touched(changes, 'style.innerShadows')) said.push('Inner shadow changed')
  if (touched(changes, 'style.tints')) said.push('Tint changed')
  if (touched(changes, 'style.text')) said.push('Text style changed')

  const blurEnabled = find(changes, 'style.blur.enabled')
  if (blurEnabled) {
    said.push(blurEnabled.after === true ? 'Blur added' : 'Blur removed')
  } else if (touched(changes, 'style.blur')) {
    said.push('Blur changed')
  }

  const opacity = find(changes, 'style.opacity')
  if (opacity) {
    said.push(
      `Opacity changed from ${Math.round(
        asNumber(opacity.before) * 100
      )}% to ${Math.round(asNumber(opacity.after) * 100)}%`
    )
  }

  if (find(changes, 'style.blendMode')) said.push('Blend mode changed')

  return said
}

/**
 * The same, run together, where there is only room for one line.
 *
 * Each phrase is written to start a line of its own, so every one after the
 * first is lowered here - "Renamed from “Card”, Moved 20px left" reads as two
 * sentences that both lost their full stop.
 */
export function describeChanges(changes: readonly Change[]): string {
  return listChanges(changes)
    .map((phrase, index) =>
      index === 0 ? phrase : phrase.charAt(0).toLowerCase() + phrase.slice(1)
    )
    .join(', ')
}

/**
 * Names the version a frame or layer is only in. The pair's own ends rather
 * than a date: two versions are often saved on the same day, and "Only on Jan
 * 20" twice says nothing.
 */
export const describeOnlyIn = (kind: 'added' | 'removed') =>
  kind === 'added' ? 'Only on latest' : 'Only on oldest'
